import { useQuery } from '@tanstack/react-query';
import { getTaskManagerTasks } from 'API/linkedin-funnel';
import { AxiosError } from 'axios';
import { useUserData } from 'hooks/use-user-data';
import { useEffect } from 'react';
import useErrorStore, { CustomErrorClass } from 'stores/error-store';
import { Tabs, Tasks } from './task-manager.types';

const defaultTaskCounts: Tasks = {
  fixAccounts: undefined,
  fixAccountsCount: undefined,
  approveContacts: undefined,
  approveContactsCount: undefined,
  approveMessages: undefined,
  approveMessagesCount: undefined,
  replyMessages: undefined,
  replyMessagesCount: undefined,
  totalCount: undefined,
};

/** Keine Konten ausgewählt: keine Aufgaben. */
const noTasks: Tasks = {
  fixAccounts: [],
  fixAccountsCount: 0,
  approveContacts: [],
  approveContactsCount: 0,
  approveMessages: [],
  approveMessagesCount: 0,
  replyMessages: [],
  replyMessagesCount: 0,
  totalCount: 0,
};

const useTasks = (funnelType: string | undefined, allowed: boolean, selectedLinkedinAccounts: string[], activeTaskFilter: Tabs) => {
  // Ohne ausgewähltes Konto wird nichts geladen. Vorher ging die Anfrage ohne Kontofilter
  // hinaus, und das Backend lieferte die Aufgaben ALLER Konten.
  const noneSelected = allowed && selectedLinkedinAccounts.length === 0;
  const { token } = useUserData();

  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);

  const {
    data: tasks = defaultTaskCounts,
    error,
    isError,
    isLoading,
    isRefetching,
  } = useQuery({
    queryKey: ['tasks', token, funnelType, activeTaskFilter, selectedLinkedinAccounts],
    queryFn: ({ signal }) => getTaskManagerTasks(funnelType, activeTaskFilter, selectedLinkedinAccounts, false, signal),
    select: (res) => res ?? defaultTaskCounts,
    refetchInterval: 15 * 60 * 1000,
    staleTime: 15 * 60 * 1000,
    enabled: allowed && !noneSelected,
  });

  useEffect(() => {
    if (isError) {
      const customError = new CustomErrorClass(error as AxiosError);
      handleOpenErrorDialog(customError, customError.request, customError.requestType);
    }
  }, [isError, error]);

  return noneSelected ? { tasks: noTasks, isLoading: false, isRefetching: false } : { tasks, isLoading, isRefetching };
};

export default useTasks;
